<template>
  <div data-page-name="zero-trust-data-resilience">
    <main>
      <div class="wrapper">
        <section class="hero">
          <NuxtImg
            preload
            class="quarter-orange-sphere"
            src="/img/landings/sphere-01.png"
            alt="quarter orange sphere"
            width="773px"
            height="976px"
            sizes="xs:0 md:0 xl:773"
            densities="x1"
            format="webp"
          />

          <div class="left">
            <header>
              <NuxtLink :to="localePath('/')">
                <img
                  src="/img/landings/logo-ootbi.svg"
                  alt="Object First logo"
                  width="179"
                  height="59"
                />
              </NuxtLink>
            </header>

            <h1>{{ $t('zero-trust-data-resilience.hero.title') }}</h1>
            <strong>{{
              $t('zero-trust-data-resilience.hero.subtitle')
            }}</strong>

            <ul>
              <li>
                <SvgIcon name="check" />
                <span>{{
                  $t('zero-trust-data-resilience.hero.list.item-1')
                }}</span>
              </li>

              <li>
                <SvgIcon name="check" />
                <span>{{
                  $t('zero-trust-data-resilience.hero.list.item-2')
                }}</span>
              </li>

              <li>
                <SvgIcon name="check" />
                <span>{{
                  $t('zero-trust-data-resilience.hero.list.item-3')
                }}</span>
              </li>

              <li>
                <SvgIcon name="check" />
                <span>{{
                  $t('zero-trust-data-resilience.hero.list.item-4')
                }}</span>
              </li>
            </ul>

            <Buttons.Regular
              fill="solid"
              accent="orange"
              class="nowrap"
              id="downloadAsset"
              @click="setOverlay({ visible: true })"
            >
              {{ $t('Download_White_Paper') }}
              <SvgIcon name="arrow-forward" />
            </Buttons.Regular>
          </div>

          <div class="right">
            <NuxtImg
              preload
              src="/img/landings/book-cover2.png"
              alt="Book cover: Zero Trust and Enterprise Data Backup"
              width="592"
              height="755"
              densities="x1 x2"
              format="webp"
            />
          </div>
        </section>

        <section class="attacks-target">
          <h2>{{ $t('zero-trust-data-resilience.attacks-target.title') }}</h2>
          <p>
            {{ $t('zero-trust-data-resilience.attacks-target.description') }}
          </p>
        </section>

        <section class="collaboration">
          <h2>{{ $t('zero-trust-data-resilience.collaboration.title') }}</h2>
          <p
            v-html="$t('zero-trust-data-resilience.collaboration.description')"
          />
        </section>

        <section class="ztdr-about">
          <p>{{ $t('zero-trust-data-resilience.ztdr-about.intro') }}</p>
          <h3>{{ $t('zero-trust-data-resilience.ztdr-about.title') }}:</h3>

          <ul>
            <li>
              {{ $t('zero-trust-data-resilience.ztdr-about.list.item-1') }}
            </li>
            <li>
              {{ $t('zero-trust-data-resilience.ztdr-about.list.item-2') }}
            </li>
            <li>
              {{ $t('zero-trust-data-resilience.ztdr-about.list.item-3') }}
            </li>
            <li>
              {{ $t('zero-trust-data-resilience.ztdr-about.list.item-4') }}
            </li>
          </ul>

          <p>{{ $t('zero-trust-data-resilience.ztdr-about.outro') }}:</p>

          <NuxtLink
            href="https://www.veeam.com/wp-zero-trust-data-resilience-research-brief.html"
            target="_blank"
          >
            https://go.veeam.com/zero-trust-data-resilience
          </NuxtLink>
        </section>

        <section class="purple">
          <div class="left">
            <strong>{{ $t('zero-trust-data-resilience.purple.title') }}</strong>
          </div>

          <div class="right">
            <Buttons.Regular
              fill="solid"
              accent="orange"
              class="nowrap"
              id="downloadAsset"
              @click="setOverlay({ visible: true })"
            >
              {{ $t('Download_White_Paper') }}
              <SvgIcon name="arrow-forward" />
            </Buttons.Regular>
          </div>
        </section>
      </div>
    </main>

    <FooterLanding />
  </div>

  <ClientOnly>
    <Teleport to="#overlay">
      <Transition name="scale-fade">
        <Forms.Wrapper @close="setOverlay({ visible: false })">
          <template #title>
            <strong>
              {{
                isSubmitted
                  ? $t('Download_Asset')
                  : $t('Provide_your_details_to_get_the_asset')
              }}
            </strong>
          </template>

          <transition name="fade" mode="out-in">
            <Modals.DownloadAsset
              v-if="isSubmitted"
              :to="localePdfPath('ZERO_TRUST')"
              short
            />

            <Forms.GetAsset
              v-else
              page="zero-trust-data-resilience"
              @loading="setLoading"
              @submitted="makeSubmitted"
            />
          </transition>
        </Forms.Wrapper>
      </Transition>
    </Teleport>
  </ClientOnly>
</template>

<script setup lang="ts">
// components
import Buttons from '~/components/redesign/Buttons';
import Forms from '~/components/redesign/Forms';
import Modals from '~/components/redesign/Modals';
import FooterLanding from '~/components/redesign/FooterLanding.vue';

// composables
import { useLocalePath } from '#i18n';
import useApiSeo from '~/composables/useApiSeo';
import useLocalePdfPath from '~/composables/useLocalePdfPath';

// stores
import useUIStore from '~/stores/ui';

const localePath = useLocalePath();
const localePdfPath = useLocalePdfPath();

const uiStore = useUIStore();
const { setOverlay, setLoading } = uiStore;

const isSubmitted = ref(false);
const makeSubmitted = () => {
  isSubmitted.value = true;
  setLoading(false);
};

definePageMeta({ layout: 'empty' });
useApiSeo('zero-trust-data-resilience');
</script>

<style scoped lang="scss">
@import '$/mixins/flex';
@import '$/mixins/media';
@import '$/mixins/size';
@import '$/mixins/typography';
@import '$/functions/token';
@import '$/variables/fonts';
@import '$/variables/shadows';

@mixin text {
  font-family: map-get($fonts, 'Inter');
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.875rem;
}

@mixin link {
  @include text;

  color: token('link');
  text-decoration: underline;

  transition: background-color 0.15s ease;

  &:hover {
    background-color: rgba(token('link'), 16%);
  }
}

@mixin h5 {
  font-family: map-get($fonts, 'Poppins');
  font-size: 2rem;
  line-height: 2.375rem;
}

@mixin h6 {
  font-family: map-get($fonts, 'Poppins');
  font-size: 1.75rem;
  line-height: 2.25rem;
}

[data-page-name='zero-trust-data-resilience'] {
  overflow: hidden;

  main {
    .wrapper {
      padding: 0 1rem;
      @include wrapper(80rem);

      section.hero {
        position: relative;
        padding: 5rem 0 8rem 0;

        @include flex-stretch-sb;
        gap: 2rem;

        @include media(1000px) {
          flex-direction: column;
          padding: 2.5rem 0 5rem 0;
          gap: 5rem;
        }

        @include mobile {
          padding: 1rem 0 2rem 0;
          gap: 2.5rem;
        }

        img.quarter-orange-sphere {
          position: absolute;
          top: 0;
          right: -6rem;
          z-index: -1;

          @include media(1000px) {
            display: none;
          }
        }

        .left {
          header {
            margin-bottom: 8rem;

            @include tablet {
              margin-bottom: 5rem;
            }

            @include mobile {
              margin-bottom: 2.5rem;
            }

            a {
              display: block;
            }
          }

          h1 {
            @include h2;
            margin-bottom: 1.5rem;

            @include mobile {
              margin-bottom: 0.5rem;
            }
          }

          strong {
            @include h6;

            display: block;
            margin-bottom: 2rem;

            @include mobile {
              margin-bottom: 1.5rem;
            }
          }

          ul {
            margin-bottom: 3rem;

            @include mobile {
              margin-bottom: 1.5rem;
            }

            li {
              @include flex-center-start;
              gap: 0.5rem;
              margin-bottom: 0.5rem;

              svg {
                @include fixed-size(1.875rem);
              }

              span {
                @include text;
                font-weight: 700;
              }
            }
          }
        }

        .right {
          @include wrapper(37rem);

          img {
            @include mobile {
              width: 100%;
              height: auto;
            }
          }
        }
      }

      section.attacks-target,
      section.collaboration {
        @include fluid-max-width(58rem);

        h2 {
          @include h5;
          margin-bottom: 1.25rem;
        }

        &:deep(p) {
          @include text;

          a {
            @include link;
          }
        }
      }

      section.attacks-target {
        margin-bottom: 5rem;

        @include mobile {
          margin-bottom: 2.5rem;
        }
      }

      section.collaboration {
        margin-bottom: 8rem;

        @include tablet {
          margin-bottom: 5rem;
        }

        @include mobile {
          margin-bottom: 2.5rem;
        }
      }

      section.ztdr-about {
        padding: 4rem;
        margin-bottom: 8rem;
        box-shadow: map-get($shadows, 'Elevation 3');

        @include media(1050px) {
          padding: 2.5rem;
          margin-bottom: 5rem;
        }

        @include mobile {
          padding: 1.5rem;
          margin-left: -1rem;
          margin-bottom: 2.5rem;
          width: calc(100% + 2rem);
        }

        p {
          @include text;

          &:first-of-type {
            margin-bottom: 3rem;
          }
        }

        h3 {
          @include h5;
          margin-bottom: 1.25rem;
        }

        ul {
          list-style: disc;
          padding-left: 1rem;
          margin-bottom: 2rem;

          li {
            @include text;
          }
        }

        a {
          @include link;
        }
      }

      section.purple {
        padding: 4rem;
        background-color: token('surf-cont-secon-action');

        @include flex-center-sb;
        gap: 2.5rem;

        position: relative;
        margin-bottom: -4rem;

        @include media(1050px) {
          gap: 2rem;
          padding: 2.5rem;
          flex-direction: column;
        }

        @include mobile {
          gap: 1.5rem;
          padding: 1.5rem;
          margin-left: -1rem;
          margin-bottom: 0;
          width: calc(100% + 2rem);
        }

        .left {
          strong {
            @include h6;
            color: token('text-reversed');
          }
        }
      }
    }
  }

  [data-component-name='FooterLanding'] {
    padding-top: 8rem;

    @include mobile {
      padding-top: 4rem;
    }
  }
}
</style>
